import StateError from './StateError';
import { setWasCalled, wasCalled } from './wasCalledUtils';

/**
 * Returns a method decorator whose target will throw if called after any of the specified
 * precluding methods have been called.
 *
 * @remarks
 * This is useful for enforcing that some methods of an object are only ever called in a specific
 * order.
 *
 * @param precludingMethodName The name of the first precluding method, i.e., the name of the first
 * method which will cause the decorated method to throw if invoked after.
 * @param morePrecludingMethodNames The names of more precluding methods, if desired.
 *
 * @example
 * ```ts
 * class Logger {
 *   private prefix: string = '';
 *
 *   @notAfter('log') // Logger.configure will throw if called after Logger.log.
 *   configure(prefix: string) {
 *     this.prefix = prefix;
 *   }
 *
 *   log(message: string) {
 *     console.log(this.prefix + message);
 *   }
 * }
 *
 * const logger1 = new Logger();
 * logger1.configure('message: ');
 * logger1.log('a'); // prints 'message: a'
 *
 * const logger2 = new Logger();
 * logger2.log('a'); // prints 'a'
 * logger2.configure('message: '); // throws
 * ```
 */
export default function notAfter<M extends string>(
  precludingMethodName: M,
  ...morePrecludingMethodNames: M[]
) {
  const precludingMethodNames = [precludingMethodName, ...morePrecludingMethodNames];
  return function(
    target: {
      [N in M]: (...args: any[]) => any;
    },
    propertyKey: string,
    descriptor: TypedPropertyDescriptor<(...args: any[]) => any>
  ) {
    // Replace the definitions of each precluding method with one that tracks whether it's been called
    precludingMethodNames.forEach(methodName => {
      // The precluding method, i.e., the method which if invoked before the decorated method, will
      // cause the decorated method to throw
      const originalPrecludingMethod = target[methodName];
      Object.defineProperty(target, methodName, {
        value(...args: any[]) {
          // Indicate that the precluding method has been invoked
          setWasCalled(this, methodName);
          // Invoke the original method
          return originalPrecludingMethod.apply(this, args);
        }
      });
    });

    // The original decorated method
    const originalMethod = descriptor.value;

    // Replace the original method's definition with one that throws if any of the precluding
    // methods have been called
    descriptor.value = function(...args: any[]) {
      precludingMethodNames.forEach(methodName => {
        // A precluding method was invoked before the decorated method; throw an error
        if (wasCalled(this, methodName)) {
          throw new StateError(
            `${target.constructor.name}.${propertyKey} may not be called after ${target.constructor.name}.${methodName} has been called.`
          );
        }
      });

      // We checked all precluding methods; invoke original decorated method
      return originalMethod && originalMethod.apply(this, args);
    };
  };
}
